<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { Lock, Eye, Search, Loader2, PackageOpen } from 'lucide-vue-next'
import { debounce } from 'lodash'
import axios from 'axios'

interface TeacherContent {
  id: number
  title: string
  slug: string
  description: string
  thumbnail_url: string
  formatted_duration: string
  view_count: number
  subject: {
    id: number
    name: string
    slug: string
    color: string
  }
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
}

interface Props {
  tutorSlug: string
  tutorName?: string
}

const props = defineProps<Props>()

const contents = ref<TeacherContent[]>([])
const subjects = ref<Subject[]>([])
const selectedSubjectId = ref<number | null>(null)
const searchQuery = ref('')
const loading = ref(false)
const initialLoading = ref(true)
const loadingMore = ref(false)
const currentPage = ref(1)
const lastPage = ref(1)
const total = ref(0)

const hasMore = computed(() => currentPage.value < lastPage.value)
const isEmpty = computed(() => !loading.value && !initialLoading.value && contents.value.length === 0)

const fetchContent = async (page: number = 1, append: boolean = false) => {
  if (append) {
    loadingMore.value = true
  } else if (page === 1) {
    initialLoading.value = true
  } else {
    loading.value = true
  }

  try {
    const params: any = { page }

    if (selectedSubjectId.value) {
      params.subject_id = selectedSubjectId.value
    }

    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }

    const response = await axios.get(`/tutors/${props.tutorSlug}/content`, { params })

    if (append) {
      contents.value = [...contents.value, ...response.data.data]
    } else {
      contents.value = response.data.data
    }

    currentPage.value = response.data.meta.current_page
    lastPage.value = response.data.meta.last_page
    total.value = response.data.meta.total
  } catch (error) {
    console.error('Failed to fetch content:', error)
  } finally {
    loading.value = false
    initialLoading.value = false
    loadingMore.value = false
  }
}

const fetchSubjects = async () => {
  try {
    const response = await axios.get(`/tutors/${props.tutorSlug}/content/subjects`)
    subjects.value = response.data.data
  } catch (error) {
    console.error('Failed to fetch subjects:', error)
  }
}

const loadMore = () => {
  if (!hasMore.value || loadingMore.value) return
  fetchContent(currentPage.value + 1, true)
}

const handleSubjectChange = () => {
  currentPage.value = 1
  fetchContent(1, false)
}

const debouncedSearch = debounce(() => {
  currentPage.value = 1
  fetchContent(1, false)
}, 500)

watch(searchQuery, () => {
  debouncedSearch()
})

watch(selectedSubjectId, handleSubjectChange)

onMounted(() => {
  fetchSubjects()
  fetchContent()
})

const getContentUrl = (content: TeacherContent) => {
  return `/library/${content.slug}`
}
</script>

<template>
  <div>
    <!--
      Nothing here may be wider than the column it sits in. The card around the
      tabs clips its overflow, and a clipped box is still a scroll container:
      focusing the search field or a card inside an overflowing panel made the
      browser scroll that hidden box sideways, leaving the tab's content offset
      with no scrollbar to bring it back. A fixed three-column grid and a fixed
      256px search field were both wider than the column on anything under a
      large laptop, which is why this tab and no other one misbehaved.
    -->
    <div class="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <h3 class="text-lg font-bold text-ink">{{ tutorName ? `${tutorName}’s library` : 'Content library' }}</h3>

      <div class="flex w-full flex-col gap-2.5 sm:w-auto sm:flex-row sm:items-center">
        <select
          v-model="selectedSubjectId"
          aria-label="Filter by subject"
          class="min-w-0 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm font-medium text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <option :value="null">All subjects</option>
          <option
            v-for="subject in subjects"
            :key="subject.id"
            :value="subject.id"
          >
            {{ subject.name }}
          </option>
        </select>

        <div class="relative min-w-0">
          <Search class="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search this library"
            aria-label="Search this library"
            class="w-full min-w-0 rounded-md border border-line bg-surface-2 py-2 pl-9 pr-3 text-sm text-ink placeholder:text-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:w-56"
          >
        </div>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="initialLoading" class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <div
        v-for="i in 6"
        :key="i"
        class="relative overflow-hidden rounded-xl bg-surface-2 animate-pulse"
      >
        <div class="aspect-video bg-line" />
        <div class="p-4 space-y-2">
          <div class="h-4 w-3/4 rounded bg-line" />
          <div class="h-3 w-1/2 rounded bg-line" />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <PackageOpen class="mb-4 size-12 text-subtle" />
      <h3 class="mb-2 text-base font-bold text-ink">Nothing here yet</h3>
      <p class="max-w-md text-sm text-muted">
        <template v-if="searchQuery || selectedSubjectId">
          No material matches that search. Try a different subject or clear the search.
        </template>
        <template v-else>
          This tutor has not published any material yet.
        </template>
      </p>
    </div>

    <!-- Content Grid -->
    <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <a
        v-for="content in contents"
        :key="content.id"
        :href="getContentUrl(content)"
        class="relative bg-media rounded-xl overflow-hidden group cursor-pointer hover:ring-2 hover:ring-brand transition-all"
      >
        <div class="aspect-video relative">
          <img
            v-if="content.thumbnail_url"
            :src="content.thumbnail_url"
            :alt="content.title"
            class="w-full h-full object-cover"
          >
          <!--
            The fallback when a video has no thumbnail. It used to read "Premium
            Content", which is now wrong twice over: the library is included in
            both plans rather than sold as one, and the label appeared on any
            video missing an image regardless of who could watch it.
          -->
          <div v-else class="flex size-full items-center justify-center bg-media">
            <div class="text-center">
              <div class="mx-auto mb-3 grid size-14 place-items-center rounded-full bg-white/10 backdrop-blur-sm">
                <Lock class="size-7 text-white" />
              </div>
              <p class="text-sm font-medium text-white/80">Included with a plan</p>
            </div>
          </div>
        </div>
        <div class="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div class="absolute bottom-0 left-0 right-0 p-4">
          <p class="text-white font-medium mb-1 line-clamp-2">{{ content.title }}</p>
          <div class="flex items-center justify-between text-sm text-white/80">
            <span>{{ content.formatted_duration }}</span>
            <span class="flex items-center gap-1">
              <Eye class="w-4 h-4" />
              {{ content.view_count.toLocaleString() }}
            </span>
          </div>
        </div>
      </a>
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore && !initialLoading" class="mt-6 text-center">
      <button
        @click="loadMore"
        :disabled="loadingMore"
        class="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-bold text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Loader2 v-if="loadingMore" class="w-5 h-5 animate-spin" />
        <span>{{ loadingMore ? 'Loading...' : 'Load More' }}</span>
      </button>
    </div>
  </div>
</template>
